import { useState, useEffect } from "react";
import { Search, Phone, MessageSquare, Car, Clock, AlertCircle, CheckCircle, ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PrivacyBlock } from "@/components/PrivacyBlock";
import { WhatsAppContactBlock } from "@/components/WhatsAppContactBlock";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorLookupPage() {
  const [plate, setPlate] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [generalContact, setGeneralContact] = useState(null);
  // Iter 151 — Jefe de Carrocería (contacto adicional cuando hay asesor encontrado)
  const [bodyshopContact, setBodyshopContact] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyError, setPrivacyError] = useState(null);

  // Request form
  const [reqName, setReqName] = useState("");
  const [reqPhone, setReqPhone] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqMessage, setReqMessage] = useState("");
  const [reqSent, setReqSent] = useState(false);
  const [reqPrivacyAccepted, setReqPrivacyAccepted] = useState(false);
  const [reqPrivacyError, setReqPrivacyError] = useState(null);
  const [reqCommercial, setReqCommercial] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/config/privacy`).then(r => setPrivacy(r.data)).catch(() => {});
  }, []);

  const doSearch = async () => {
    const cleanPlate = plate.replace(/[\s-]/g, "").toUpperCase();
    const cleanPhone = phone.replace(/[\s()-]/g, "");
    if (!cleanPlate || cleanPlate.length < 4) {
      toast.error("Introduce una matricula valida");
      return;
    }
    if (!cleanPhone || cleanPhone.length < 9) {
      toast.error("Introduce un telefono movil valido");
      return;
    }
    if (!privacyAccepted) {
      setPrivacyError("Debes aceptar la politica de privacidad para continuar");
      return;
    }
    setPrivacyError(null);
    setLoading(true);
    setResult(null);
    setShowRequestForm(false);
    try {
      const r = await axios.post(`${API}/api/public/advisor-lookup`, {
        plate: cleanPlate,
        phone: cleanPhone,
      });
      setResult(r.data);
      if (!r.data.found || !r.data.advisor) {
        const gc = await axios.get(`${API}/api/public/general-contact`);
        setGeneralContact(gc.data);
      } else {
        // Iter 151 — sí hay asesor: pedir también el contacto del Jefe de Carrocería
        // (sólo se expone cuando está activado y tiene teléfono en backend).
        try {
          const gc = await axios.get(`${API}/api/public/general-contact`);
          setBodyshopContact(gc.data?.bodyshop_contact || null);
        } catch { setBodyshopContact(null); }
      }
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error("Demasiadas solicitudes. Intentelo de nuevo en unos minutos.");
      } else {
        toast.error(privacy?.privacy_error_generic || "No se ha podido validar la informacion facilitada.");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitRequest = async () => {
    if (!reqName || !reqPhone) {
      toast.error("Nombre y telefono son obligatorios");
      return;
    }
    if (!reqPrivacyAccepted) {
      setReqPrivacyError("Debes aceptar la politica de privacidad para continuar");
      return;
    }
    setReqPrivacyError(null);
    try {
      await axios.post(`${API}/api/public/request-attention`, {
        client_name: reqName,
        client_phone: reqPhone,
        client_email: reqEmail,
        plate: plate,
        message: reqMessage,
      });
      setReqSent(true);
      toast.success("Solicitud enviada correctamente");
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error("Demasiadas solicitudes. Intentelo de nuevo en unos minutos.");
      } else {
        toast.error(e.response?.data?.detail || "Error al enviar solicitud");
      }
    }
  };

  const reset = () => {
    setResult(null);
    setPlate("");
    setPhone("");
    setShowRequestForm(false);
    setReqSent(false);
    setPrivacyAccepted(false);
    setPrivacyError(null);
    setReqPrivacyAccepted(false);
    setReqPrivacyError(null);
    setReqCommercial(false);
    setBodyshopContact(null);
  };

  const errorMessage = privacy?.privacy_error_generic || "No se ha podido validar la informacion facilitada. Revisa la matricula y el telefono movil o solicita atencion directa.";

  // Determine contact info for CTA
  const advisorWhatsApp = result?.advisor?.whatsapp_phone;
  const advisorPhone = result?.advisor?.phone;
  const advisorEmail = result?.advisor?.email;
  const hasWhatsApp = !!advisorWhatsApp;
  const hasPhone = !!advisorPhone;

  return (
    <div className="min-h-[70vh] flex flex-col" data-testid="advisor-lookup-page">
      {/* Hero */}
      <div className="bg-slate-900 text-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="heading-industrial text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mb-4" data-testid="lookup-title">
            Seguimiento de tu vehiculo
          </h1>
          <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Consulta el estado de tu vehiculo o contacta directamente con tu asesor de servicio.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 -mt-8 mb-16">
        <div className="bg-white border border-slate-200 rounded-sm shadow-lg p-6 sm:p-8" data-testid="lookup-search-card">

          {!result && !showRequestForm && !reqSent && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-4 h-4 text-slate-400" />
                <p className="text-xs text-slate-500">Para proteger tu privacidad, necesitamos dos datos de verificacion.</p>
              </div>

              {/* Plate + Phone fields */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1 block">Matricula del vehiculo *</label>
                  <div className="relative">
                    <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={plate}
                      onChange={e => setPlate(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && doSearch()}
                      placeholder="Ej: 1234ABC"
                      className="pl-10 h-12 rounded-sm text-base font-mono uppercase"
                      data-testid="lookup-plate-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1 block">Telefono movil *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && doSearch()}
                      placeholder="Ej: 611 222 333"
                      className="pl-10 h-12 rounded-sm text-base font-mono"
                      data-testid="lookup-phone-input"
                    />
                  </div>
                </div>

                {/* Privacy Block - INSIDE form, BEFORE button */}
                <PrivacyBlock
                  privacyText={privacy?.privacy_form_text_advisor}
                  policyUrl={privacy?.privacy_policy_url || "/politica-de-privacidad"}
                  contactEmail={privacy?.privacy_contact_email}
                  checkboxText={privacy?.privacy_checkbox_text || "He leido y acepto la"}
                  accepted={privacyAccepted}
                  onAcceptedChange={(v) => { setPrivacyAccepted(v); setPrivacyError(null); }}
                  error={privacyError}
                />

                <Button
                  onClick={doSearch}
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-12 text-sm uppercase tracking-wider font-bold"
                  data-testid="lookup-search-btn"
                >
                  {loading ? "Verificando..." : "Consultar"}
                </Button>
              </div>
            </>
          )}

          {/* Results - FOUND WITH ADVISOR */}
          {result?.found && result.advisor && (
            <div className="space-y-5" data-testid="lookup-result">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Asesor encontrado</span>
              </div>

              {result.advisor?.is_backup && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-700" data-testid="lookup-backup-notice">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Su asesor habitual ({result.advisor.original_advisor_name}) no esta disponible temporalmente. Le atiende su suplente.
                </div>
              )}

              {result.advisor && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-6" data-testid="lookup-advisor-card">
                  <div className="flex items-start gap-4">
                    <img
                      src={getImageUrl(result.advisor.avatar_url)}
                      alt={result.advisor.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 flex-shrink-0"
                      data-testid="lookup-advisor-avatar"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">{result.advisor.name}</h3>
                      <p className="text-sm text-slate-600">{result.advisor.title}</p>
                      {result.advisor.center && <p className="text-xs text-slate-500 mt-1">Centro: {result.advisor.center}</p>}
                      {result.advisor.working_hours_text && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          {result.advisor.working_hours_text}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {result.assignment && (
                <div className="text-xs text-slate-500 px-2">
                  {result.assignment.vehicle_plate && <span className="font-mono font-bold mr-2">{result.assignment.vehicle_plate}</span>}
                  {result.assignment.vehicle_info && <span>{result.assignment.vehicle_info}</span>}
                </div>
              )}

              {/* Iter 195 — Microcopy basado en horario REAL del asesor */}
              {(hasWhatsApp || hasPhone || advisorEmail) && (() => {
                const sched = result.advisor?.schedule;
                const dayKey = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"][new Date().getDay()];
                const hhmm = (() => { const n = new Date(); return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; })();
                const hasStructured = sched && Array.isArray(sched.schedule_days) && sched.schedule_start && sched.schedule_end;
                let state = "unknown"; // in | out | unknown
                if (hasStructured) {
                  const dayMatches = sched.schedule_days.includes(dayKey);
                  const inRange = hhmm >= sched.schedule_start && hhmm < sched.schedule_end;
                  state = (dayMatches && inRange) ? "in" : "out";
                }
                return (
                  <div className="bg-slate-50 border-l-2 border-slate-300 rounded-sm px-4 py-3" data-testid="lookup-advisor-microcopy">
                    {state === "in" && (
                      <p className="text-[12px] leading-relaxed text-slate-600" data-testid="lookup-advisor-microcopy-in-hours">
                        Tu asesor está disponible en horario de atención. Escríbele por WhatsApp y te responderá lo antes posible.
                      </p>
                    )}
                    {state === "out" && (
                      <p className="text-[12px] leading-relaxed text-slate-600" data-testid="lookup-advisor-microcopy-out-of-hours">
                        Ahora estamos fuera del horario de atención. Puedes dejar tu mensaje por WhatsApp y tu asesor te responderá el siguiente día laborable.
                      </p>
                    )}
                    {state === "unknown" && (
                      <p className="text-[12px] leading-relaxed text-slate-600" data-testid="lookup-advisor-microcopy-neutral">
                        Tu asesor recibirá tu mensaje y te atenderá lo antes posible dentro de su horario habitual.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* WhatsApp Contact Block with dynamic message */}
              {hasWhatsApp && (
                <WhatsAppContactBlock
                  phone={advisorWhatsApp}
                  clientName={result.client_name}
                  plate={result.assignment?.vehicle_plate}
                  vehicleInfo={result.assignment?.vehicle_info}
                  label="Hablar por WhatsApp"
                  testIdPrefix="lookup-wa"
                />
              )}

              {/* Fallback: phone call when no WhatsApp */}
              {!hasWhatsApp && hasPhone && (
                <a href={`tel:${advisorPhone}`} target="_top" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-14 text-sm uppercase tracking-wider font-bold shadow-md hover:shadow-lg transition-all" data-testid="lookup-call-btn">
                  <Phone className="w-5 h-5" />
                  Llamar a tu asesor
                </a>
              )}

              {/* Fallback: email when no phone at all */}
              {!hasWhatsApp && !hasPhone && advisorEmail && (
                <a href={`mailto:${advisorEmail}`} target="_top" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-14 text-sm uppercase tracking-wider font-bold shadow-md hover:shadow-lg transition-all" data-testid="lookup-email-btn">
                  Enviar email a tu asesor
                </a>
              )}

              {/* Iter 151 — Jefe de Carrocería (solo si activo y con teléfono) */}
              {bodyshopContact && bodyshopContact.active && bodyshopContact.phone && (
                <div className="border-t border-slate-200 pt-5 mt-2 space-y-3" data-testid="lookup-bodyshop-section">
                  <div className="bg-amber-50/50 border border-amber-200 rounded-sm p-5" data-testid="lookup-bodyshop-card">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-5 h-5 text-amber-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-slate-900" data-testid="lookup-bodyshop-name">{bodyshopContact.name}</h3>
                        <p className="text-xs text-amber-800 font-semibold uppercase tracking-wider mt-0.5">{bodyshopContact.role || "Jefe de Carrocería"}</p>
                        <p className="text-xs text-slate-600 mt-2">{bodyshopContact.help_text || "Para consultas de chapa, pintura, partes o peritaciones."}</p>
                        {bodyshopContact.center && <p className="text-[11px] text-slate-500 mt-1">Centro: {bodyshopContact.center}</p>}
                        {bodyshopContact.hours && (
                          <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-slate-500">
                            <Clock className="w-3 h-3" />
                            {bodyshopContact.hours}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Iter 195 — Microcopy de carrocería con horario real si disponible */}
                  {(() => {
                    const sched = bodyshopContact.schedule;
                    const dayKey = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"][new Date().getDay()];
                    const hhmm = (() => { const n = new Date(); return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; })();
                    const hasStructured = sched && Array.isArray(sched.schedule_days) && sched.schedule_start && sched.schedule_end;
                    let state = "unknown";
                    if (hasStructured) {
                      const dayMatches = sched.schedule_days.includes(dayKey);
                      const inRange = hhmm >= sched.schedule_start && hhmm < sched.schedule_end;
                      state = (dayMatches && inRange) ? "in" : "out";
                    }
                    let text;
                    if (state === "in") {
                      text = "El responsable de carrocería está disponible en su horario. Escríbele por WhatsApp y te responderá lo antes posible.";
                    } else if (state === "out") {
                      text = "Ahora estamos fuera del horario de carrocería. Puedes dejar tu mensaje y el responsable te responderá el siguiente día laborable.";
                    } else {
                      text = "Para consultas de chapa, pintura, partes o peritaciones, puedes escribir directamente al responsable de carrocería. Te atenderá lo antes posible dentro de su horario.";
                    }
                    return (
                      <div className="bg-amber-50/30 border-l-2 border-amber-300 rounded-sm px-4 py-3" data-testid="lookup-bodyshop-microcopy" data-state={state}>
                        <p className="text-[12px] leading-relaxed text-slate-700">{text}</p>
                      </div>
                    );
                  })()}
                  {(() => {
                    const tpl = bodyshopContact.wa_message_template
                      || "Hola, soy {client_name}. Quería hacer una consulta de carrocería sobre mi vehículo {vehicle_model} con matrícula {plate}.";
                    const cname = result.client_name || "cliente de Ascauto";
                    const plateVal = result.assignment?.vehicle_plate || "";
                    const vinfo = result.assignment?.vehicle_info || "";
                    // Si la plantilla incluye {vehicle_model} pero no hay vehicle_info, dejamos un fallback elegante
                    const safeVehicle = vinfo || "vehículo";
                    const msg = tpl
                      .replace(/\{client_name\}/g, cname)
                      .replace(/\{plate\}/g, plateVal)
                      .replace(/\{vehicle_model\}/g, safeVehicle)
                      .replace(/\{vehicle_info\}/g, safeVehicle);
                    const href = `https://wa.me/${bodyshopContact.phone_clean}?text=${encodeURIComponent(msg)}`;
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-amber-600 hover:bg-amber-700 text-white rounded-sm h-14 text-sm uppercase tracking-wider font-bold shadow-md hover:shadow-lg transition-all"
                        data-testid="lookup-bodyshop-wa-btn">
                        <MessageSquare className="w-5 h-5" />
                        Hablar con Carrocería por WhatsApp
                      </a>
                    );
                  })()}
                </div>
              )}

              {/* SECONDARY: Nueva consulta */}
              <div className="flex items-center justify-center pt-2">
                <button
                  onClick={reset}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="lookup-reset-btn"
                >
                  <ArrowLeft className="w-3 h-3" /> Nueva consulta
                </button>
              </div>
            </div>
          )}

          {/* Results - FOUND BUT NO ADVISOR (Case B) */}
          {result?.found && !result.advisor && (
            <div className="space-y-5" data-testid="lookup-no-advisor-result">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Vehiculo localizado</span>
              </div>

              {result.assignment && (
                <div className="text-xs text-slate-500 px-2">
                  {result.assignment.vehicle_plate && <span className="font-mono font-bold mr-2">{result.assignment.vehicle_plate}</span>}
                  {result.assignment.vehicle_info && <span>{result.assignment.vehicle_info}</span>}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-sm p-5" data-testid="lookup-no-advisor-msg">
                <p className="text-sm text-blue-800 leading-relaxed">
                  Hemos localizado tu vehiculo correctamente, pero todavia no tienes un asesor asignado. Puedes contactar ahora con nuestro equipo y te atenderemos de inmediato.
                </p>
              </div>

              {(generalContact?.whatsapp_phone || generalContact?.phone) && (
                <WhatsAppContactBlock
                  phone={generalContact.whatsapp_phone || generalContact.phone}
                  clientName={result.client_name}
                  plate={result.assignment?.vehicle_plate}
                  vehicleInfo={result.assignment?.vehicle_info}
                  label="Contactar con posventa"
                  testIdPrefix="lookup-general-wa"
                />
              )}

              {!generalContact?.whatsapp_phone && !generalContact?.phone && generalContact?.email && (
                <div className="text-xs text-slate-500 space-y-1 px-2">
                  <p>Email: <a href={`mailto:${generalContact.email}`} className="text-slate-900 font-medium hover:underline">{generalContact.email}</a></p>
                </div>
              )}

              <div className="flex items-center justify-center gap-4 pt-2">
                <button onClick={() => setShowRequestForm(true)} className="text-xs text-slate-500 hover:text-slate-700 underline transition-colors" data-testid="lookup-request-assignment-btn">
                  Solicitar que me asignen un asesor
                </button>
                <span className="text-slate-300">|</span>
                <button onClick={reset} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="lookup-reset-btn-no-advisor">
                  <ArrowLeft className="w-3 h-3" /> Nueva consulta
                </button>
              </div>
            </div>
          )}

          {/* Results - NOT FOUND */}
          {result && !result.found && (
            <div className="space-y-4" data-testid="lookup-not-found">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-semibold text-amber-700">Verificacion no completada</span>
              </div>
              <p className="text-xs text-slate-500">{errorMessage}</p>

              <div className="space-y-3">
                <Button
                  onClick={() => setShowRequestForm(true)}
                  variant="outline"
                  className="w-full rounded-sm h-11 text-xs uppercase tracking-wider font-semibold border-slate-300"
                  data-testid="lookup-request-attention-btn"
                >
                  Solicitar atencion
                </Button>
                {(generalContact?.whatsapp_phone || generalContact?.phone) && (
                  <WhatsAppContactBlock
                    phone={generalContact.whatsapp_phone || generalContact.phone}
                    clientName=""
                    plate={plate}
                    vehicleInfo=""
                    label="WhatsApp general"
                    testIdPrefix="lookup-notfound-wa"
                  />
                )}
              </div>

              <div className="flex items-center justify-center pt-2">
                <button onClick={reset} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="lookup-reset-btn">
                  <ArrowLeft className="w-3 h-3" /> Nueva consulta
                </button>
              </div>
            </div>
          )}

          {/* Request Form */}
          {showRequestForm && !reqSent && (
            <div data-testid="lookup-request-form">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Solicitar atencion</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium">Nombre completo *</label>
                  <Input value={reqName} onChange={e => setReqName(e.target.value)} className="rounded-sm" data-testid="lookup-req-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 font-medium">Telefono *</label>
                    <Input value={reqPhone} onChange={e => setReqPhone(e.target.value)} className="rounded-sm" data-testid="lookup-req-phone" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium">Email</label>
                    <Input value={reqEmail} onChange={e => setReqEmail(e.target.value)} className="rounded-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium">Mensaje</label>
                  <textarea value={reqMessage} onChange={e => setReqMessage(e.target.value)} className="w-full border border-slate-200 rounded-sm p-2 text-sm h-20 resize-none" placeholder="Describe brevemente tu consulta..." />
                </div>

                {/* Privacy Block for request form */}
                <PrivacyBlock
                  privacyText={privacy?.privacy_form_text_contact}
                  policyUrl={privacy?.privacy_policy_url || "/politica-de-privacidad"}
                  contactEmail={privacy?.privacy_contact_email}
                  checkboxText={privacy?.privacy_checkbox_text || "He leido y acepto la"}
                  accepted={reqPrivacyAccepted}
                  onAcceptedChange={(v) => { setReqPrivacyAccepted(v); setReqPrivacyError(null); }}
                  error={reqPrivacyError}
                  showCommercial={true}
                  commercialText={privacy?.privacy_commercial_text}
                  commercialAccepted={reqCommercial}
                  onCommercialChange={setReqCommercial}
                />

                <Button
                  onClick={submitRequest}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-xs uppercase tracking-wider font-semibold"
                  data-testid="lookup-submit-request-btn"
                >
                  Enviar solicitud
                </Button>
              </div>
            </div>
          )}

          {reqSent && (
            <div className="text-center py-8" data-testid="lookup-request-sent">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-slate-900 mb-1">Solicitud enviada</h3>
              <p className="text-sm text-slate-500">Nos pondremos en contacto con usted lo antes posible.</p>
              <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-700 mt-4 underline" data-testid="lookup-new-search-btn">
                Nueva consulta
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
